import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { useId } from "react";
import { cn } from "@/lib/utils";
import { AiSparkles, Icon, type IconName } from "../icon";
import { ButtonSpinner } from "../spinner";

const buttonVariants = cva(
  [
    "inline-flex",
    "items-center",
    "justify-center",
    "whitespace-nowrap",
    "shrink-0",
    "rounded-(--radius-sm)",
    "px-(--spacing-12)",
    "border",
    "border-solid",
    "font-(family-name:--font-family-label)",
    "font-(number:--font-weight-medium)",
    "cursor-pointer",
    "select-none",
    "transition-[background,color,box-shadow]",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-button-primary-default-background",
          "text-button-primary-default-text",
          "border-button-primary-default-background",
          "hover:bg-button-primary-hover-background",
          "hover:border-button-primary-hover-background",
          "active:bg-button-primary-pressed-background",
          "active:border-button-primary-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        "conversation-ui": [
          "bg-button-primary-conversation-ui-background",
          "text-button-primary-conversation-ui-text",
          "border-button-primary-conversation-ui-background",
          "hover:bg-button-primary-conversation-ui-background",
          "hover:border-button-primary-conversation-ui-background",
          "active:bg-button-primary-conversation-ui-background",
          "active:border-button-primary-conversation-ui-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        outline: [
          "bg-button-outline-default-background",
          "text-button-outline-default-text",
          "border-button-outline-default-border",
          "hover:bg-button-outline-hover-background",
          "hover:border-button-outline-hover-border",
          "active:bg-button-outline-pressed-background",
          "active:border-button-outline-pressed-border",
          "data-[mouse-down]:bg-button-outline-pressed-background",
          "data-[mouse-down]:border-button-outline-pressed-border",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        ghost: [
          "bg-transparent",
          "text-button-ghost-default-text",
          "border-transparent",
          "hover:bg-button-ghost-hover-background",
          "active:bg-button-ghost-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        info: [
          "bg-button-info-default-background",
          "text-button-info-default-text",
          "border-button-info-default-background",
          "hover:bg-button-info-hover-background",
          "hover:border-button-info-hover-background",
          "active:bg-button-info-pressed-background",
          "active:border-button-info-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        "ai-outline": [
          "aw-ai-button--outline",
          "bg-surface",
          "border-transparent",
          "text-text-default",
          "hover:bg-button-outline-hover-background",
          "active:bg-button-outline-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
        "ai-ghost": [
          "border-transparent",
          "bg-transparent",
          "text-text-default",
          "hover:bg-surface-hover",
          "active:bg-button-ghost-pressed-background",
          "focus-visible:ring-2",
          "focus-visible:ring-offset-1",
          "focus-visible:ring-offset-surface",
          "focus-visible:ring-button-focus-ring",
        ],
      },
      intent: {
        default: null,
        destructive: null,
      },
      size: {
        sm: [
          "h-(--spacing-32)",
          "gap-(--spacing-8)",
          "text-(length:--font-size-body-medium)",
          "leading-(--font-line-height-20)",
        ],
        md: [
          "h-(--spacing-40)",
          "gap-(--spacing-8)",
          "text-(length:--font-size-body-large)",
          "leading-(--font-line-height-24)",
        ],
        lg: [
          "h-[44px]",
          "gap-(--spacing-8)",
          "px-(--spacing-16)",
          "text-(length:--font-size-body-large)",
          "leading-(--font-line-height-24)",
        ],
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        intent: "destructive",
        class: [
          "bg-button-primary-destructive-default-background",
          "text-button-primary-destructive-text",
          "border-button-primary-destructive-default-background",
          "hover:bg-button-primary-destructive-hover-background",
          "hover:border-button-primary-destructive-hover-background",
          "active:bg-button-primary-destructive-pressed-background",
          "active:border-button-primary-destructive-pressed-background",
        ],
      },
      {
        variant: "ghost",
        intent: "destructive",
        class: ["text-text-destructive"],
      },
      {
        variant: ["ai-outline", "ai-ghost"],
        size: "sm",
        class: [
          "text-(length:--font-size-body-large)",
          "leading-(--font-line-height-20)",
        ],
      },
      {
        variant: ["ai-outline", "ai-ghost"],
        size: "md",
        class: ["leading-(--font-line-height-20)"],
      },
      {
        variant: ["ai-outline", "ai-ghost"],
        size: "lg",
        class: ["px-(--spacing-12)", "leading-(--font-line-height-20)"],
      },
    ],
    defaultVariants: {
      variant: "primary",
      intent: "default",
      size: "md",
    },
  },
);

function AiOutlineBorder() {
  const id = useId().replace(/:/g, "");
  const gradientId = `ai-button-gradient-${id}`;
  const shimmerGradientId = `ai-button-shimmer-${id}`;
  const glowId = `ai-button-glow-${id}`;

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-0 size-full overflow-visible"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientTransform="rotate(-12 .5 .5)"
        >
          <stop
            offset="13%"
            stopColor="hsl(var(--primary-primary-container))"
          />
          <stop offset="44%" stopColor="hsl(var(--primary-primary))" />
          <stop offset="100%" stopColor="hsl(var(--tertiary-tertiary))" />
        </linearGradient>
        <linearGradient
          id={shimmerGradientId}
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientTransform="rotate(-12 .5 .5)"
        >
          <stop
            offset="0%"
            stopColor="hsl(var(--primary-primary))"
            stopOpacity="0"
          />
          <stop
            offset="30%"
            stopColor="hsl(var(--primary-primary-container))"
            stopOpacity="0"
          />
          <stop
            offset="46%"
            stopColor="hsl(var(--gradients-ai-gradient-01))"
            stopOpacity="0.9"
          />
          <stop
            offset="70%"
            stopColor="hsl(var(--tertiary-tertiary))"
            stopOpacity="0"
          />
          <stop
            offset="100%"
            stopColor="hsl(var(--tertiary-tertiary))"
            stopOpacity="0"
          />
          <animateTransform
            attributeName="gradientTransform"
            type="rotate"
            from="-12 .5 .5"
            to="348 .5 .5"
            dur="3.25s"
            repeatCount="indefinite"
          />
        </linearGradient>
        <filter
          id={glowId}
          x="-2"
          y="-2"
          width="calc(100% + 4px)"
          height="calc(100% + 4px)"
          filterUnits="userSpaceOnUse"
        >
          <feGaussianBlur
            in="SourceGraphic"
            stdDeviation="0.8"
            result="haloBlur"
          />
          <feFlood
            floodColor="hsl(var(--gradients-ai-gradient-01))"
            floodOpacity="0.4"
            result="haloColor"
          />
          <feComposite
            in="haloColor"
            in2="haloBlur"
            operator="in"
            result="halo"
          />
          <feMerge>
            <feMergeNode in="halo" />
          </feMerge>
        </filter>
        <mask
          id={`${glowId}-outside`}
          x="-2"
          y="-2"
          width="calc(100% + 4px)"
          height="calc(100% + 4px)"
          maskUnits="userSpaceOnUse"
        >
          <rect
            x="-2"
            y="-2"
            width="calc(100% + 4px)"
            height="calc(100% + 4px)"
            fill="white"
          />
          <rect
            className="aw-ai-button__border"
            x="0.5"
            y="0.5"
            width="calc(100% - 1px)"
            height="calc(100% - 1px)"
            fill="black"
          />
        </mask>
      </defs>
      <g mask={`url(#${glowId}-outside)`} filter={`url(#${glowId})`}>
        <rect
          className="aw-ai-button__border"
          x="0.5"
          y="0.5"
          width="calc(100% - 1px)"
          height="calc(100% - 1px)"
          fill="none"
          stroke="hsl(var(--gradients-ai-gradient-01))"
          strokeWidth="4"
        />
      </g>
      <rect
        className="aw-ai-button__border"
        x="0.5"
        y="0.5"
        width="calc(100% - 1px)"
        height="calc(100% - 1px)"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="1"
      />
      <rect
        className="aw-ai-button__border aw-ai-button__highlight"
        x="0.5"
        y="0.5"
        width="calc(100% - 1px)"
        height="calc(100% - 1px)"
        fill="none"
        stroke={`url(#${shimmerGradientId})`}
        strokeWidth="1.5"
      />
    </svg>
  );
}

type InternalButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;
type ButtonVariant = Exclude<InternalButtonVariant, "conversation-ui">;
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;
type PublicButtonSize = Exclude<ButtonSize, "lg">;

interface ButtonBaseProps
  extends Omit<ButtonPrimitive.Props, "className" | "size">,
    Omit<VariantProps<typeof buttonVariants>, "icon" | "size" | "variant"> {
  /** Button content */
  children: React.ReactNode;
  /** Loading state — shows spinner, disables interaction */
  loading?: boolean;
  /** Text shown during loading. "..." is appended automatically. Defaults to "Loading" ("Thinking" for AI variants). */
  loadingText?: string;
  /** Stretch to full width of container */
  fullWidth?: boolean;
}

type ButtonPropsWithSize<TSize extends ButtonSize> = ButtonBaseProps & {
  size?: TSize;
};

export type ButtonProps =
  | (ButtonPropsWithSize<PublicButtonSize> & {
      /** AI variants always display the AI sparkle. */
      variant: "ai-outline" | "ai-ghost";
      icon?: never;
    })
  | (ButtonPropsWithSize<PublicButtonSize> & {
      variant?: Exclude<ButtonVariant, "ai-outline" | "ai-ghost">;
      /** Optional icon displayed after the label. */
      icon?: IconName;
    });

type MenuButtonTriggerButtonProps =
  | (ButtonPropsWithSize<ButtonSize> & {
      variant: "ai-outline" | "ai-ghost";
      icon?: never;
    })
  | (ButtonPropsWithSize<ButtonSize> & {
      variant?: Exclude<ButtonVariant, "ai-outline" | "ai-ghost">;
      icon?: IconName;
    });

type ButtonRootProps =
  | (ButtonPropsWithSize<ButtonSize> & {
      variant: "ai-outline" | "ai-ghost";
      icon?: never;
    })
  | (ButtonPropsWithSize<ButtonSize> & {
      variant?: Exclude<InternalButtonVariant, "ai-outline" | "ai-ghost">;
      icon?: IconName;
    });

type ConversationButtonProps = Omit<ButtonProps, "variant">;

/**
 * Action button with standard and AI visual styles plus interaction states.
 *
 * @example
 * <Button variant="primary">Save</Button>
 * <Button variant="ghost" intent="destructive">Delete</Button>
 * <Button variant="ai-outline">Ask Reli</Button>
 * <Button loading loadingText="Saving">Save</Button>
 */
function ButtonRoot({
  children,
  variant,
  intent,
  size,
  icon,
  loading = false,
  loadingText,
  fullWidth = false,
  disabled = false,
  ...props
}: ButtonRootProps) {
  const isDisabled = disabled || loading;
  const isAiVariant = variant === "ai-outline" || variant === "ai-ghost";
  const isAiOutline = variant === "ai-outline";
  const resolvedLoadingText =
    loadingText ?? (isAiVariant ? "Thinking" : "Loading");
  const contents = (
    <>
      {loading ? `${resolvedLoadingText}...` : children}
      {loading ? (
        <ButtonSpinner size="sm" />
      ) : isAiVariant ? (
        <span
          className="inline-flex size-(--icon-sm) shrink-0 overflow-hidden"
          aria-hidden="true"
        >
          <AiSparkles className="block size-(--icon-sm) shrink-0" size="sm" />
        </span>
      ) : icon ? (
        <Icon name={icon} size="sm" tone="inherit" />
      ) : null}
    </>
  );

  return (
    <ButtonPrimitive
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({ variant, intent, size }),
        fullWidth && "w-full",
        isDisabled && "opacity-40 pointer-events-none",
        disabled && "cursor-not-allowed",
        loading && !disabled && "cursor-default",
      )}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      tabIndex={isDisabled ? -1 : undefined}
      {...props}
    >
      {isAiOutline ? <AiOutlineBorder /> : null}
      {isAiVariant ? (
        <span className="relative z-10 inline-flex items-center gap-(--spacing-8)">
          {contents}
        </span>
      ) : (
        contents
      )}
    </ButtonPrimitive>
  );
}

function Button(props: ButtonProps) {
  return <ButtonRoot {...props} />;
}

/** Internal trigger used by MenuButton. Not exported from @assetwatch/ui. */
function MenuButtonTriggerButton(props: MenuButtonTriggerButtonProps) {
  return <ButtonRoot {...props} />;
}

/** Internal conversation-styled Button used by ChatActionButton. */
function ConversationButton(props: ConversationButtonProps) {
  return <ButtonRoot {...props} variant="conversation-ui" />;
}

export { Button, ConversationButton, MenuButtonTriggerButton };
